import { useMemo, useState } from 'react';
import { DollarSign, TrendingDown, TrendingUp, Info, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils/cn';
import {
  prorateFreight,
  BASIS_LABEL,
  BASIS_EXPLANATION,
  freightDelta,
  type ProrationBasis,
  type ProrationItem,
} from '../utils/container-freight-proration';

/**
 * Impacto del armado en el costo por producto.
 *
 * Es el cierre del círculo y el momento donde el armado 3D deja de ser un dibujo:
 * la ocupación real del contenedor define el prorrateo del flete, y ese prorrateo
 * define el costo nacionalizado por unidad.
 *
 * Antes el prorrateo usaba el volumen tipeado a mano; ahora sale de cómo entran
 * las cajas de verdad, incluido el aire que queda entre ellas — ese aire también
 * viaja y también se paga.
 */

export interface FreightImpactPanelProps {
  /**
   * El flete que le corresponde a ESTE contenedor.
   *
   * Si la orden lleva varios, es su parte — no el flete del embarque completo.
   * Ver `containerSplit`.
   */
  freightTotal: number;
  /**
   * Cómo se repartió el flete del embarque entre los contenedores.
   *
   * `undefined` cuando la orden tiene uno solo: ahí decir "le corresponden 10.440
   * de 10.440" es ruido. Con dos o más hay que explicarlo, porque el número que se
   * ve no es el que el cliente tiene en la cotización.
   */
  containerSplit?: {
    orderFreight: number;
    position: number;
    total: number;
    basis: 'occupied' | 'capacity' | 'equal';
  };
  currency?: string;
  items: ProrationItem[];
  /** Volumen que consume el armado, del 3D. `null` si todavía no se armó. */
  occupiedVolumeCbm: number | null;
  containerVolumeCbm: number | null;
  /**
   * Flete por unidad que se usó al cotizar, si lo hay.
   *
   * Es lo que permite decir si la cotización que se le mandó al cliente se
   * sostiene: sin esto se muestra el costo, pero no si cambió.
   */
  estimatedPerUnit?: Record<string, number>;
}

const BASES: ProrationBasis[] = ['volume', 'weight', 'value', 'units'];

/** Cómo se decidió la parte de cada contenedor, dicho en castellano. */
const SPLIT_BASIS_LABEL: Record<'occupied' | 'capacity' | 'equal', string> = {
  occupied: 'según lo que ocupa cada uno de verdad',
  capacity: 'según el tamaño de cada equipo, porque ninguno está armado todavía',
  equal: 'en partes iguales, porque falta el armado y el tamaño de los equipos',
};

/**
 * El reparto de dos niveles, dicho de frente.
 *
 * Sin esto el operador ve 7.277 en la pantalla y 10.439 en la cotización que le
 * mandó al cliente, y no tiene forma de saber por qué no coinciden.
 */
function SplitNotice({
  split,
  amount,
  fmt,
  currency = 'USD',
}: {
  split: NonNullable<FreightImpactPanelProps['containerSplit']>;
  amount: number;
  fmt: (n: number) => string;
  currency?: string;
}) {
  return (
    <p className="w-full rounded-lg border border-prixia-sky/40 bg-prixia-sky/10 px-3 py-2 text-xs leading-relaxed">
      <strong>
        Contenedor {split.position} de {split.total}.
      </strong>{' '}
      El embarque completo cotizó {currency} {fmt(split.orderFreight)} de flete, y a este contenedor
      le corresponden{' '}
      <strong className="font-mono">
        {currency} {fmt(amount)}
      </strong>{' '}
      — repartido {SPLIT_BASIS_LABEL[split.basis]}.
    </p>
  );
}

export function FreightImpactPanel({
  freightTotal,
  containerSplit,
  currency = 'USD',
  items,
  occupiedVolumeCbm,
  containerVolumeCbm,
  estimatedPerUnit,
}: FreightImpactPanelProps) {
  const [basis, setBasis] = useState<ProrationBasis>('volume');

  /** Sin FOB no se puede repartir por valor. Ver el botón deshabilitado abajo. */
  const hasFob = useMemo(() => items.some((i) => i.unitFobValue > 0), [items]);

  const result = useMemo(
    () =>
      prorateFreight({
        freightTotal,
        items,
        basis,
        occupiedVolumeCbm,
        containerVolumeCbm,
      }),
    [freightTotal, items, basis, occupiedVolumeCbm, containerVolumeCbm]
  );

  const fmt = (n: number) =>
    n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <DollarSign className="h-4 w-4 text-prixia-blue" />
            Impacto en el costo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          <p className="text-xs text-muted-foreground">
            Cargá los productos del contenedor y el flete se reparte entre ellos. Ese reparto es lo
            que da el costo por unidad.
          </p>
          {/*
            La parte que le toca se muestra igual, aunque todavía no haya
            productos. Es información útil desde antes: el operador sabe con qué
            monto va a trabajar cuando estibe.
          */}
          {containerSplit && <SplitNotice split={containerSplit} amount={freightTotal} fmt={fmt} />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-prixia-blue" />
              Impacto en el costo
            </CardTitle>
            <CardDescription className="text-xs">
              El flete de {currency} {fmt(freightTotal)} repartido entre los productos.
            </CardDescription>
          </div>

          {/*
            El reparto de dos niveles, dicho de frente.

            Sin esto el usuario ve 13.920 en la pantalla y 20.880 en la
            cotización que le mandó al cliente, y no tiene forma de saber por qué
            no coinciden.
          */}
          {containerSplit && (
            <SplitNotice
              split={containerSplit}
              amount={freightTotal}
              fmt={fmt}
              currency={currency}
            />
          )}

          {/* Ocupación: el dato que aporta el armado */}
          {result.fillPercent !== null ? (
            <div className="text-right">
              <p className="font-mono text-lg font-semibold leading-none tabular-nums text-prixia-ink">
                {result.fillPercent}%
              </p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                ocupación real
              </p>
            </div>
          ) : (
            <p className="max-w-[13rem] text-right text-[11px] text-muted-foreground">
              Sin armar todavía: se reparte por el volumen declarado de cada producto.
            </p>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Criterio del reparto */}
        <div>
          <div className="flex flex-wrap gap-1.5">
            {BASES.map((b) => {
              // Repartir por valor sin tener el FOB daría partes iguales, y eso
              // se leería como "el reparto por valor es parejo" cuando en
              // realidad es que falta el dato. Se traba y se dice.
              const disabled = b === 'value' && !hasFob;
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBasis(b)}
                  disabled={disabled}
                  aria-pressed={basis === b}
                  title={
                    disabled
                      ? 'Los productos del contenedor no tienen el FOB cargado. Está en la pestaña Productos de la orden.'
                      : undefined
                  }
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                    basis === b
                      ? 'border-prixia-ink bg-prixia-ink text-white'
                      : 'border-border hover:border-prixia-silver',
                    disabled && 'cursor-not-allowed opacity-40'
                  )}
                >
                  {BASIS_LABEL[b]}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            {BASIS_EXPLANATION[basis]}
          </p>
        </div>

        {/* El reparto */}
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/50">
              <tr className="border-b text-xs">
                <th className="px-3 py-2 text-left font-medium">Producto</th>
                <th className="px-3 py-2 text-right font-medium">Cant.</th>
                <th className="px-3 py-2 text-right font-medium">Participación</th>
                <th className="px-3 py-2 text-right font-medium">Flete</th>
                <th className="px-3 py-2 text-right font-medium">Por unidad</th>
                {estimatedPerUnit && (
                  <th className="px-3 py-2 text-right font-medium">vs cotizado</th>
                )}
              </tr>
            </thead>
            <tbody>
              {result.items.map((item) => {
                const estimated = estimatedPerUnit?.[item.id];
                const delta =
                  estimated !== undefined ? freightDelta(estimated, item.freightPerUnit) : null;

                return (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs">{item.description}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                      {item.quantity}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {(item.share * 100).toFixed(1)}%
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                      {fmt(item.freightTotal)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold tabular-nums text-prixia-ink">
                      {fmt(item.freightPerUnit)}
                    </td>
                    {estimatedPerUnit && (
                      <td className="px-3 py-2 text-right">
                        {delta === null ? (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        ) : (
                          <Delta absolute={delta.absolute} percent={delta.percent} />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30">
                <td className="px-3 py-2 text-xs font-semibold" colSpan={3}>
                  Total repartido
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs font-semibold tabular-nums">
                  {fmt(result.distributedTotal)}
                </td>
                <td colSpan={estimatedPerUnit ? 2 : 1} />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Volumen: de dónde sale el reparto */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-lg bg-muted/30 px-3 py-2 text-[11px]">
          <span className="text-muted-foreground">
            Volumen de las cajas:{' '}
            <strong className="font-mono text-foreground">{result.netVolumeCbm} m³</strong>
          </span>
          {result.occupiedVolumeCbm !== null && (
            <span className="text-muted-foreground">
              Ocupa en el contenedor:{' '}
              <strong className="font-mono text-foreground">{result.occupiedVolumeCbm} m³</strong>
              <span className="ml-1">— incluye el aire entre las cajas, que también viaja</span>
            </span>
          )}
        </div>

        <p className="text-[10px] leading-relaxed text-muted-foreground">
          Este flete por unidad es el que entra al CIF en la pestaña de Costos, y de ahí al costo
          nacionalizado. Cambiar el criterio de reparto cambia el costo de cada producto, pero nunca
          el total.
        </p>
      </CardContent>
    </Card>
  );
}

/** La diferencia contra lo cotizado, con el signo bien claro. */
function Delta({ absolute, percent }: { absolute: number; percent: number | null }) {
  if (Math.abs(absolute) < 0.01) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Minus className="h-3 w-3" />
        igual
      </span>
    );
  }

  const worse = absolute > 0;
  const Icon = worse ? TrendingUp : TrendingDown;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono text-[11px] font-semibold tabular-nums',
        // Que el costo real suba es malo para el margen, aunque la flecha sea
        // "hacia arriba". El color va por el negocio, no por la dirección.
        worse ? 'text-bloqueo' : 'text-ok'
      )}
      title={
        worse
          ? 'El costo real es mayor al cotizado: el margen se comprime.'
          : 'El costo real es menor al cotizado: se cotizó con holgura.'
      }
    >
      <Icon className="h-3 w-3" />
      {absolute > 0 ? '+' : ''}
      {absolute.toFixed(2)}
      {percent !== null && <span className="opacity-70">({percent}%)</span>}
    </span>
  );
}
